import {
  MoreVertical,
  Edit,
  Trash2,
  Check,
  X,
  Heart,
  MessageCircle,
  Image as ImageIcon,
  Send,
} from "lucide-react";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addComment, deletePost, toggleLike, updatePost } from "../../../features/postSlice";
import API from "../../../utils/Api";
import { Link } from "react-router-dom";

const Post = ({
  username,
  avatar,
  postId,
  userId,
  time,
  content,
  imageUrl,
  likes = [],
  comments = [],
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);

  const isOwner = user?._id === userId;

  const [openMenu, setOpenMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentImage, setCommentImage] = useState(null);
  const [commentPreview, setCommentPreview] = useState("");

  const handleSave = () => {
    if (!editedContent.trim()) return;

    dispatch(
      updatePost({
        postId,
        content: editedContent,
      })
    );

    setIsEditing(false);
  };

  const apiBase = API.defaults.baseURL?.replace(/\/api\/?$/, "") || "";
  const resolvedImageUrl = imageUrl
    ? imageUrl.startsWith("http")
      ? imageUrl
      : `${apiBase}${imageUrl}`
    : "";

  const resolveAssetUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `${apiBase}${url}`;
  };

  const currentUserId = user?._id;
  const isLiked = likes?.some((like) => {
    const likeId = like?._id || like;
    return likeId?.toString() === currentUserId;
  });

  const handleLike = () => {
    if (!postId) return;
    dispatch(toggleLike(postId));
  };

  const handleCommentImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (commentPreview) {
      URL.revokeObjectURL(commentPreview);
    }

    setCommentImage(file);
    setCommentPreview(URL.createObjectURL(file));
  };

  const handleAddComment = () => {
    if (!commentText.trim() && !commentImage) return;

    dispatch(
      addComment({
        postId,
        text: commentText.trim(),
        imageFile: commentImage,
      })
    );

    setCommentText("");
    setCommentImage(null);
    setCommentPreview("");
  };

  return (
    <div className="bg-white rounded-md border border-gray-200 mb-6 relative">

      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-5 pt-5">
        <div className="flex items-center">
          {avatar ? (
            <img
              src={resolveAssetUrl(avatar)}
              alt="Avatar"
              className="w-10 h-10 rounded-full object-cover mr-3"
            />
          ) : (
            <div className="w-10 h-10 bg-indigo-200 rounded-full flex items-center justify-center text-sm font-bold text-indigo-700 mr-3">
              {username?.charAt(0)}
            </div>
          )}

          <div>
            <div className="flex items-center gap-2">
              <Link
                to={`/profile/${userId}`}
                className="font-bold text-gray-900 hover:text-indigo-600"
              >
                {username}
              </Link>
              <Link
                to={`/chat/${userId}`}
                className="text-xs text-indigo-600 hover:text-indigo-700"
              >
                Message
              </Link>
            </div>
            <p className="text-sm text-gray-500">{time}</p>
          </div>
        </div>

        {/* Menu */}
        {isOwner && !isEditing && (
          <div className="relative">
            <button
              onClick={() => setOpenMenu(!openMenu)}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <MoreVertical size={18} />
            </button>

            {openMenu && (
              <div className="absolute right-0 mt-2 w-32 bg-white border rounded-lg shadow-lg z-10">
                <button
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50"
                  onClick={() => {
                    setIsEditing(true);
                    setOpenMenu(false);
                  }}
                >
                  <Edit size={16} /> Edit
                </button>

                <button
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  onClick={() => dispatch(deletePost(postId))}
                >
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      {!isEditing ? (
        <div>
          {content && (
            <div className="px-5">
              <p className="text-gray-800 whitespace-pre-line">
                {content}
              </p>
            </div>
          )}
          {resolvedImageUrl && (
            <img
              src={resolvedImageUrl}
              alt="Post"
              className="w-full max-h-[480px] object-cover border-t border-b border-gray-200 mt-3"
            />
          )}
        </div>
      ) : (
        <div className="space-y-2 px-5">
          <textarea
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            rows={4}
            className="w-full border rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                setIsEditing(false);
                setEditedContent(content);
              }}
              className="flex items-center gap-1 px-3 py-1 text-sm border rounded-md hover:bg-gray-50"
            >
              <X size={16} /> Cancel
            </button>

            <button
              onClick={handleSave}
              className="flex items-center gap-1 px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              <Check size={16} /> Save
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-6 text-sm text-gray-600 mt-4 px-5 pb-4">
        <button
          onClick={handleLike}
          className={`flex items-center gap-1 hover:text-indigo-600 ${
            isLiked ? "text-indigo-600" : ""
          }`}
        >
          <Heart size={18} className={isLiked ? "fill-indigo-600" : ""} />
          <span>{likes?.length || 0}</span>
        </button>

        <button
          onClick={() => setShowComments((prev) => !prev)}
          className="flex items-center gap-1 hover:text-indigo-600"
        >
          <MessageCircle size={18} />
          <span>{comments?.length || 0}</span>
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="mt-4 border-t pt-4 px-5 pb-5 space-y-4">
          <div className="space-y-2">
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows={2}
              placeholder="Write a comment..."
              className="w-full border rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />

            <div className="flex items-center justify-between">
              <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer hover:text-indigo-600">
                <ImageIcon size={16} />
                Add Photo
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleCommentImageChange}
                />
              </label>

              {commentImage && (
                <button
                  type="button"
                  onClick={() => {
                    if (commentPreview) {
                      URL.revokeObjectURL(commentPreview);
                    }
                    setCommentImage(null);
                    setCommentPreview("");
                  }}
                  className="text-xs text-red-500 hover:text-red-600"
                >
                  Remove photo
                </button>
              )}
            </div>

            {commentPreview && (
              <img
                src={commentPreview}
                alt="Comment preview"
                className="w-full max-h-72 object-cover rounded-md border"
              />
            )}

            <button
              onClick={handleAddComment}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              <Send size={16} />
              Comment
            </button>
          </div>

          <div className="space-y-3">
            {comments?.map((comment, index) => (
              <div key={comment._id || index} className="flex gap-3">
                {comment.author?.avatarUrl ? (
                  <img
                    src={resolveAssetUrl(comment.author.avatarUrl)}
                    alt={comment.author?.name || comment.author?.username || "User"}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 bg-indigo-200 rounded-full flex items-center justify-center text-xs font-bold text-indigo-700">
                    {(comment.author?.name || comment.author?.username || "U").charAt(0)}
                  </div>
                )}

                <div className="flex-1 bg-gray-50 border rounded-md p-3">
                  <p className="text-sm font-semibold text-gray-800">
                    {comment.author?.name || comment.author?.username || "User"}
                  </p>
                  {comment.text && (
                    <p className="text-sm text-gray-700 whitespace-pre-line mt-1">
                      {comment.text}
                    </p>
                  )}
                  {comment.imageUrl && (
                    <img
                      src={resolveAssetUrl(comment.imageUrl)}
                      alt="Comment"
                      className="w-full max-h-80 object-cover rounded-md border mt-2"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Post;
